import React, { useEffect, useState } from "react"

import { NumericRestriction } from "../stages/NumericRestriction"
import { FreetextRestriction } from "../stages/FreetextRestriction"
import { PredefinedRestriction } from "../stages/PredefinedRestriction"
import { dataFormatsArray, displayStylesArray, textCaseArray } from "../../../utils/Constants"
import { G_onInputBlurHandler, G_onInputChangeHandler } from "../../../components/utils/InputHandlers"

export const AttributeControls = ({ data }) => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        input: {
            configuration: '',
            format: null,
            mandatory: false,
            minimum: '',
            maximum: '',
            unit_label: '',
            multiline: false,
            whole_numbers: false,
            allow_negative: false,
            allow_special: false,
            allow_mulitple: false,
            display_style: null,
            text_case: null,

            oversight: '',
            ownership: false,
            coowners: false,
            restricted: false,
            editable: false,
        },
        errors: {
            // Attribute Identity
            basic: '',
            name: '',
            attr_group: '',
            hint: '',

            // Attribute Configuration
            configuration: '',
            format: '',
            minimum: '',
            maximum: '',
            unit_label: '',
            display_style: '',

            // Oversight
            oversight: '',
        },
        show: {
            minMaxVisibility: false,
            createAttributeGroup: false,
        },
    })

    const dataControl = data.attr.s?.f

    useEffect(() => {
        let { input, errors } = state

        // Global properties
        input.mandatory = data.attr.s?.m
        input.format = dataFormatsArray
            .find((dataType: any) => dataType.key === dataControl.format)

        switch (data.attr.s?.f?.format) {
            case 'freetext':
                input.minimum = dataControl.minimum
                input.maximum = dataControl.maximum
                input.multiline = dataControl.multiline
                input.allow_special = dataControl.allow_special

                input.text_case = textCaseArray
                    .find((textCase: any) => textCase.key === dataControl.case)
                break

            case 'numeric':
                input.minimum = dataControl.minimum
                input.maximum = dataControl.maximum
                input.unit_label = dataControl.unit
                input.whole_numbers = dataControl.whole_numbers
                input.allow_negative = dataControl.allow_negative
                break

            case 'predefined':
                input.allow_mulitple = dataControl.allow_mulitple

                input.display_style = displayStylesArray
                    .find((display: any) => display.key === dataControl.display)
                break
        }

        setstate((prevState: any) => ({
            ...prevState,
            input,
        }))
    }, [])

    const minLength = 3;
    const maxLength = 200;

    const onListBoxHandler = (e: any, list: any) => {
        let { posting, errors, input, show } = state
        if (posting) return

        let updatedErrors = {
            ...errors
        }

        switch (list) {
            case 'attr_group':
                const attrGroup = data.groups.find(
                    (grp: any) => grp.g === e
                )

                updatedErrors = {
                    ...updatedErrors,
                    attr_group: '',
                }

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        attr_group: attrGroup,
                    },
                    errors: updatedErrors
                }));
                break

            case 'format':
                const isSameFormat = input.format?.key === e.key;
                const isFreeText = e.key === 'freetext';
                const isPredefined = e.key === 'predefined';

                // Pre-calculate new values
                const multiline = isFreeText ? input.multiline : false;
                const displayStyle = isPredefined ? input.display_style : { key: "radio", label: "Radio Buttons" };
                const textCase = isFreeText
                    ? input.text_case
                    : { key: 'mixed', label: 'Mixed Case' };

                const minimum = isSameFormat ? input.minimum : '';
                const maximum = isSameFormat ? input.maximum : '';

                // Reset errors if format changes
                if (!isSameFormat) {
                    updatedErrors = {
                        ...updatedErrors,
                        format: '',
                        minimum: '',
                        maximum: '',
                        unit_label: '',
                        display_style: '',
                    }
                }

                setstate((prev: any) => ({
                    ...prev,
                    input: {
                        ...prev.input,
                        format: e,
                        minimum,
                        maximum,
                        multiline,
                        display_style: displayStyle,
                        whole_numbers: false,
                        allow_negative: false,
                        allow_special: false,
                        text_case: textCase,
                    },
                    show: {
                        ...prev.show,
                        minMaxVisibility: isSameFormat ? show.minMaxVisibility : false,
                    },
                    errors: updatedErrors,
                }))
                break

            case 'display_style':
                if (input.format?.key !== 'predefined') {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: { key: "radio", label: "Radio Buttons" },
                        },
                        errors: updatedErrors
                    }));
                } else {
                    updatedErrors = {
                        ...updatedErrors,
                        display_style: '',
                    }

                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            display_style: e,
                            allow_mulitple: e?.key !== 'dropdown' ? false : input.allow_mulitple,
                        },
                        errors: updatedErrors
                    }));
                }
                break;

            case 'text_case':
                if (input.format?.key !== 'freetext') {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            text_case: {
                                key: "mixed",
                                label: "Mixed Case",
                            },
                        },
                        errors: updatedErrors
                    }));
                } else {
                    setstate((prev: any) => ({
                        ...prev,
                        input: {
                            ...prev.input,
                            text_case: e,
                        },
                        errors: updatedErrors
                    }));
                }
                break
        }

        setstate((prev: any) => ({
            ...prev,
            errors: updatedErrors,
        }))
    }

    const onChangeHandler = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        let output: any = G_onInputChangeHandler(e)

        const updatedInput = {
            ...input,
            [e.target.name]: output.value
        };

        const updatedErrors = {
            ...errors,
            [e.target.name]: output.error
        };

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const targetLength = (name: string) => {
        switch (name) {
            case 'unit_label':
                return {
                    min: 1,
                    max: 30
                };

            default:
                return {
                    min: 3,
                    max: 30
                };
        }
    }

    const onInputBlur = (e: any) => {
        let { posting, input, errors } = state
        if (posting) return

        const target0 = targetLength(e.target.name)
        const title = e.target.name === 'name' ? 'Attribute name' : ''

        let output: any = G_onInputBlurHandler(e, title, target0.min, target0.max)

        let updatedInput = {
            ...input,
        }

        let updatedErrors = {
            ...errors,
        };

        switch (e.target.name) {
            case 'minimum':
            case 'maximum':
                const format = input.format?.key

                if (!["freetext"].includes(format)) {
                    // TODO: Add validation for non-numeric and non-freetext
                    return
                }

                const targetName = e.target.name
                const targetValue = e.target.value

                if (format === 'freetext') {
                    const num = Number(targetValue);
                    const isWholeNumber = Number.isInteger(num);

                    if (!isWholeNumber) {
                        output.error = "Length must be a whole number."
                    } else {
                        // Remove the error message from blur handler
                        output.error = ''

                        if (targetName === "minimum") {
                            const newMin = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                minimum: newMin.toString(),
                                maximum:
                                    input.maximum && Number(input.maximum) < newMin
                                        ? ""
                                        : input.maximum,
                            };
                        }

                        if (targetName === "maximum") {
                            const newMax = num < minLength ? minLength
                                : num > maxLength ? maxLength
                                    : num

                            updatedInput = {
                                ...updatedInput,
                                maximum: newMax.toString(),
                                minimum:
                                    input.minimum && Number(input.minimum) > newMax
                                        ? minLength.toString()
                                        : input.minimum,
                            };
                        }
                    }
                } else if (format === 'numeric') {
                    const num = Number(targetValue);

                    // Whole number check
                    if (input.whole_numbers && !Number.isInteger(num)) {
                        output.error = "Value must be a whole number.";
                    } else if (!input.allow_negative && num < 0) {
                        // Negative number check
                        output.error = "Negative numbers are not allowed.";
                    } else {
                        // Clear error if passed checks
                        output.error = "";

                        // Update fields
                        if (targetName === "minimum") {
                            updatedInput = {
                                ...updatedInput,
                                minimum: num.toString(),
                                maximum:
                                    input.maximum && Number(input.maximum) < num
                                        ? "" // reset maximum if smaller than minimum
                                        : input.maximum,
                            };
                        }

                        if (targetName === "maximum") {
                            updatedInput = {
                                ...updatedInput,
                                maximum: num.toString(),
                                minimum:
                                    input.minimum && Number(input.minimum) > num
                                        ? "" // reset minimum if greater than maximum
                                        : input.minimum,
                            };
                        }
                    }

                }
                break;

            default:
                updatedInput = {
                    ...updatedInput,
                    [e.target.name]: output.value
                }
                break;
        }

        setstate((prev: any) => ({
            ...prev,
            input: updatedInput,
            errors: updatedErrors,
        }));
    }

    const dataTypeHandler = (format: any) => {
        switch (format) {
            case 'freetext':
                return <FreetextRestriction
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            case 'numeric':
                return <NumericRestriction
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                />

            case 'predefined':
                return <PredefinedRestriction
                    state={state}
                    setstate={setstate}
                    blurHandler={onInputBlur}
                    changeHandler={onChangeHandler}
                    listboxHandler={onListBoxHandler}
                />

            default:
                break;
        }
    }

    const onFormHandler = (e: any) => {
        e.preventDefault()
        let { posting, input, show } = state
        if (posting) return

        let valid = true
        let inputKeys = null
        let errorKeys = null

        let updatedErrors = state.errors
        const minVal = Number(input.minimum)
        const maxVal = Number(input.maximum)

        switch (dataControl.format) {
            case 'freetext':
                if (!show.minMaxVisibility) break;

                const isMinValid = Number.isInteger(minVal);
                const isMaxValid = Number.isInteger(maxVal);

                // Require both minimum and maximum values
                if (!input.minimum) {
                    valid = false;
                    updatedErrors.minimum = "Minimum value is required.";
                }
                if (!input.maximum) {
                    valid = false;
                    updatedErrors.maximum = "Maximum value is required.";
                }

                if (input.minimum && !isMinValid) {
                    valid = false;
                    updatedErrors.minimum = "Minimum length must be a whole number.";
                }

                if (input.maximum && !isMaxValid) {
                    valid = false;
                    updatedErrors.maximum = "Maximum length must be a whole number.";
                }

                if (isMinValid && (minVal < minLength || minVal > maxLength)) {
                    valid = false;
                    updatedErrors.minimum = `Minimum must be between ${minLength} and ${maxLength}.`;
                }

                if (isMaxValid && (maxVal < minLength || maxVal > maxLength)) {
                    valid = false;
                    updatedErrors.maximum = `Maximum must be between ${minLength} and ${maxLength}.`;
                }

                if (isMinValid && isMaxValid && minVal > maxVal) {
                    valid = false;
                    updatedErrors.maximum = "Maximum must be greater than or equal to minimum.";
                }
                break

            case 'numeric':
                inputKeys = [
                    'unit_label',
                ]

                inputKeys.forEach((inputKey: any) => {
                    try {
                        let oTarget = e.target[inputKey]

                        let event: any = {
                            target: {
                                name: oTarget.name,
                                required: oTarget.required,
                                value: oTarget.value.trim(),
                            }
                        }

                        onInputBlur(event)
                    } catch (error) {
                        // Override error
                    }
                })

                let { errors } = state

                errorKeys = [
                    'unit_label'
                ]

                errorKeys.forEach((errorObject: any) => {
                    if (errors[errorObject].length > 0) {
                        valid = false
                    }
                })

                if (!valid) {
                    updatedErrors.configuration = 'Kindly clear errors in section before proceeding'
                } else {
                    updatedErrors.configuration = ''
                }

                if (!show.minMaxVisibility) break;

                // Required checks
                if (!input.minimum) {
                    valid = false;
                    updatedErrors.minimum = "Minimum value is required.";
                }
                if (!input.maximum) {
                    valid = false;
                    updatedErrors.maximum = "Maximum value is required.";
                }

                // Whole numbers only
                if (input.whole_numbers) {
                    if (input.minimum && !Number.isInteger(minVal)) {
                        valid = false;
                        updatedErrors.minimum = "Minimum value must be a whole number.";
                    }
                    if (input.maximum && !Number.isInteger(maxVal)) {
                        valid = false;
                        updatedErrors.maximum = "Maximum value must be a whole number.";
                    }
                }

                // No negatives
                if (!input.allow_negative) {
                    if (input.minimum && minVal < 0) {
                        valid = false;
                        updatedErrors.minimum = "Minimum value cannot be negative.";
                    }
                    if (input.maximum && maxVal < 0) {
                        valid = false;
                        updatedErrors.maximum = "Maximum value cannot be negative.";
                    }
                }

                // Range check
                if (input.minimum && input.maximum && minVal > maxVal) {
                    valid = false;
                    updatedErrors.maximum =
                        "Maximum must be greater than or equal to minimum.";
                }
                break

            case 'predefined':
                if (!input.display_style) {
                    valid = false
                    updatedErrors.display_style = 'Kindly select a data format'
                }
                break
        }

        setstate((prev: any) => ({
            ...prev,
            errors: updatedErrors,
            posting: valid ? true : false,
        }));
    }

    return (
        <React.Fragment>
            <div className="w-full md:max-w-[512px] lg:max-w-[768px] pt-4 pb-28">
                <header className="w-full pb-4">
                    <p className="text-lg font-medium text-slate-800 dark:text-slate-100 pb-1">
                        Attribute Controls
                    </p>

                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Customize how attribute behaves
                    </p>
                </header>


                <div className="flex flex-col w-full pt-2 pb-6">
                    <p className="text-sm text-slate-800 dark:text-slate-200 truncate pb-2 uppercase">
                        Data Type
                    </p>

                    <div className="w-full">
                        {
                            dataFormatsArray
                                .filter((dataType: any) => dataType.key === data.attr.s?.f?.format)
                                .map((dataType: any) => (
                                    <div key={`attr-data-type-${dataType.key}`} className="select-none sw-full">
                                        <div className="w-full flex flex-row align-middle items-center pb-2">
                                            <div className="flex-1 flex align-middle items-center gap-x-3">
                                                <span className={`fa-duotone ${dataType.icon} flex-none fa-lg`}></span>

                                                <p className="text-slate-700 dark:text-slate-200">
                                                    {dataType.label}
                                                </p>
                                            </div>
                                        </div>

                                        <span className="block text-sm text-slate-500 dark:text-slate-400">
                                            {dataType.desc}
                                        </span>
                                    </div >
                                ))
                        }
                    </div>

                    <div className="w-full flex flex-row pt-3">
                        <button type="button" className="text-sm text-orange-500 hover:text-orange-600 dark:text-orange-500 dark:hover:text-orange-400">
                            Change Data Type
                        </button>
                    </div>
                </div>

                <form className="w-full pt-2 pb-10" onSubmit={onFormHandler}>
                    <div className="pb-4">
                        {
                            dataTypeHandler(data.attr.s?.f?.format)
                        }
                    </div>

                    <button type="submit" className="w-auto min-w-24 justify-center disabled:cursor-not-allowed text-sm rounded-md border border-transparent shadow-sm px-3 py-1.5 bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-500 focus:outline-none">
                        {
                            state.posting ? (
                                <span className="flex flex-row items-center h-5 justify-center">
                                    <i className="fad fa-spinner-third fa-xl fa-spin"></i>
                                </span>
                            ) : (
                                <>
                                    Save
                                </>
                            )
                        }
                    </button>
                </form>
            </div>
        </React.Fragment>
    )
}